import type { ChatItem } from "../components/MessageBubble";

export type ApprovalContinuationStatus =
  | "recording"
  | "recorded"
  | "continuing"
  | "retry-decision"
  | "retry";

export type ApprovalRecovery = {
  readonly approvalId: string;
  readonly approvalGroupId: string;
  readonly toolName: string;
  readonly args: unknown;
  readonly messageId: string;
  readonly messageIndex: number;
  readonly approved: boolean;
  readonly reason?: string;
  readonly responseMessageId?: string;
  readonly status: ApprovalContinuationStatus;
};

export type ApprovalContinuationPlan =
  | {
      readonly kind: "waiting";
      readonly approvalGroupId: string;
      readonly approvalIds: readonly string[];
    }
  | {
      readonly kind: "ready";
      readonly approvalGroupId: string;
      readonly approvalIds: readonly string[];
      readonly promptMessageId: string;
    };

export function canStartApprovalDecision(
  busyApprovalIds: ReadonlySet<string>,
  approvalId: string,
  status: ApprovalContinuationStatus | undefined,
): boolean {
  return (
    !busyApprovalIds.has(approvalId) &&
    (status === undefined || status === "retry" || status === "retry-decision")
  );
}

function getMergedStatus(
  item: Extract<ChatItem, { readonly kind: "approval-request" }>,
  recoveryStatus: ApprovalContinuationStatus,
): ApprovalContinuationStatus {
  if (item.continuationMessageStatus === "pending") return "continuing";
  if (item.continuationMessageStatus === "failed") return "retry";
  return recoveryStatus;
}

export function mergeApprovalRecoveries(
  items: readonly ChatItem[],
  recoveries: Readonly<Record<string, ApprovalRecovery>>,
  completedApprovalIds: readonly string[],
): ChatItem[] {
  const completedIds = new Set(completedApprovalIds);
  const visibleApprovalIds = new Set<string>();
  const merged = items.flatMap((item): ChatItem[] => {
    if (item.kind !== "approval-request") return [item];
    if (completedIds.has(item.approvalId)) return [];
    visibleApprovalIds.add(item.approvalId);
    const recovery = recoveries[item.approvalId];
    if (!recovery) return [item];
    return [
      {
        ...item,
        approvalStatus: getMergedStatus(item, recovery.status),
        retryLabel: recovery.approved ? "Retry" : "Retry denial",
        retryApproved: recovery.approved,
        retryEdit: recovery.reason === "User chose to edit",
        responseMessageId: item.responseMessageId ?? recovery.responseMessageId,
      },
    ];
  });

  Object.values(recoveries).forEach((recovery) => {
    if (
      completedIds.has(recovery.approvalId) ||
      visibleApprovalIds.has(recovery.approvalId)
    ) {
      return;
    }
    merged.push({
      kind: "approval-request",
      approvalId: recovery.approvalId,
      approvalGroupId: recovery.approvalGroupId,
      toolName: recovery.toolName,
      args: recovery.args,
      messageId: recovery.messageId,
      messageIndex: recovery.messageIndex,
      approvalStatus: recovery.status,
      retryLabel: recovery.approved ? "Retry" : "Retry denial",
      retryApproved: recovery.approved,
      retryEdit: recovery.reason === "User chose to edit",
      responseMessageId: recovery.responseMessageId,
    });
  });

  return merged;
}

export function getApprovalContinuationPlan(
  items: readonly ChatItem[],
  recoveries: Readonly<Record<string, ApprovalRecovery>>,
  latestResponse: {
    readonly approvalId: string;
    readonly responseMessageId: string;
  },
): ApprovalContinuationPlan {
  const latestItem = items.find(
    (item) =>
      item.kind === "approval-request" && item.approvalId === latestResponse.approvalId,
  );
  const latestRecovery = recoveries[latestResponse.approvalId];
  const approvalGroupId =
    latestItem?.kind === "approval-request"
      ? latestItem.approvalGroupId
      : latestRecovery?.approvalGroupId;
  if (approvalGroupId === undefined) {
    return {
      kind: "waiting",
      approvalGroupId: "",
      approvalIds: [latestResponse.approvalId],
    };
  }

  const approvalIds: string[] = [];
  const responseIds = new Map<string, string>();
  items.forEach((item) => {
    if (item.kind !== "approval-request" || item.approvalGroupId !== approvalGroupId) {
      return;
    }
    if (!approvalIds.includes(item.approvalId)) approvalIds.push(item.approvalId);
    if (item.responseMessageId !== undefined) {
      responseIds.set(item.approvalId, item.responseMessageId);
    }
  });
  Object.values(recoveries).forEach((recovery) => {
    if (recovery.approvalGroupId !== approvalGroupId) return;
    if (!approvalIds.includes(recovery.approvalId)) approvalIds.push(recovery.approvalId);
    if (recovery.responseMessageId !== undefined) {
      responseIds.set(recovery.approvalId, recovery.responseMessageId);
    }
  });
  responseIds.set(latestResponse.approvalId, latestResponse.responseMessageId);

  if (approvalIds.some((approvalId) => !responseIds.has(approvalId))) {
    return { kind: "waiting", approvalGroupId, approvalIds };
  }
  return {
    kind: "ready",
    approvalGroupId,
    approvalIds,
    promptMessageId: latestResponse.responseMessageId,
  };
}
